/**
 * PopupManager - Unified popup notification system
 *
 * Handles all transient popup notifications (acceleration, boost, miles, max speed, waves, items)
 * with consistent animation and lifecycle management.
 *
 * Performance optimizations:
 * - DOM element pooling to avoid createElement/appendChild overhead
 * - GPU-accelerated animations via will-change and translateZ
 * - Reduced text-shadow on mobile devices
 */

export class PopupManager {
    constructor() {
        this.stylesInjected = false;

        // Detect mobile for performance optimizations
        this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        // Pool for reusable popup elements (acceleration/boost popups)
        this.accelPool = [];
        this.accelPoolSize = 12; // Max simultaneous popups
        this.accelPoolIndex = 0;

        // Pool for miles popups
        this.milesPool = [];
        this.milesPoolSize = 4;
        this.milesPoolIndex = 0;

        // Pool for scrap popups (can scrap rapidly)
        this.scrapPool = [];
        this.scrapPoolSize = 8;
        this.scrapPoolIndex = 0;

        // Container for all pooled popups (avoids document.body reflows)
        this.container = null;

        this.ensureStyles();
        this.initPools();
    }

    /**
     * Initialize popup pools
     */
    initPools() {
        // Create container for game-level popups (miles from driving, etc.)
        // z-index: 150 keeps popups above game UI (100) but below modals (200+)
        this.container = document.createElement('div');
        this.container.id = 'popup-container';
        this.container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 150;
            overflow: hidden;
        `;
        document.body.appendChild(this.container);

        // Create container for modal-level popups (scrap, bonuses, etc.)
        // z-index: 250 keeps these above modals (200) but below ModalManager (10000)
        this.modalContainer = document.createElement('div');
        this.modalContainer.id = 'popup-container-modal';
        this.modalContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 250;
            overflow: hidden;
        `;
        document.body.appendChild(this.modalContainer);

        // Pre-create acceleration/boost popup elements
        for (let i = 0; i < this.accelPoolSize; i++) {
            const popup = document.createElement('div');
            popup.className = 'popup-accel';
            popup.style.cssText = `
                position: absolute;
                font-family: 'Courier New', monospace;
                font-size: 24px;
                font-weight: bold;
                pointer-events: none;
                white-space: nowrap;
                opacity: 0;
                will-change: transform, opacity;
                transform: translateZ(0);
            `;
            this.container.appendChild(popup);
            this.accelPool.push({
                el: popup,
                inUse: false,
                timeoutId: null
            });
        }

        // Pre-create miles popup elements
        for (let i = 0; i < this.milesPoolSize; i++) {
            const popup = document.createElement('div');
            popup.className = 'popup-miles';
            popup.style.cssText = `
                position: absolute;
                font-family: 'Courier New', monospace;
                font-size: 32px;
                font-weight: bold;
                pointer-events: none;
                opacity: 0;
                will-change: transform, opacity;
                transform: translateZ(0);
            `;
            this.container.appendChild(popup);
            this.milesPool.push({
                el: popup,
                inUse: false,
                timeoutId: null
            });
        }

        // Pre-create scrap popup elements (in modal container for visibility above modals)
        for (let i = 0; i < this.scrapPoolSize; i++) {
            const popup = document.createElement('div');
            popup.className = 'popup-scrap';
            popup.style.cssText = `
                position: absolute;
                font-family: 'Courier New', monospace;
                font-weight: bold;
                pointer-events: none;
                white-space: nowrap;
                opacity: 0;
                will-change: transform, opacity;
                transform: translateZ(0);
            `;
            this.modalContainer.appendChild(popup);
            this.scrapPool.push({
                el: popup,
                inUse: false,
                timeoutId: null
            });
        }
    }

    /**
     * Get next available popup from pool
     */
    getFromPool(pool, poolIndex) {
        const startIndex = poolIndex;
        let item = pool[poolIndex];

        // Find first available or force reuse oldest
        for (let i = 0; i < pool.length; i++) {
            const idx = (startIndex + i) % pool.length;
            if (!pool[idx].inUse) {
                item = pool[idx];
                return { item, nextIndex: (idx + 1) % pool.length };
            }
        }

        // All in use - force reuse the next one (oldest)
        item = pool[poolIndex];
        if (item.timeoutId) {
            clearTimeout(item.timeoutId);
        }
        // Reset animation by removing and re-adding class
        item.el.style.animation = 'none';
        item.el.offsetHeight; // Force reflow

        return { item, nextIndex: (poolIndex + 1) % pool.length };
    }

    /**
     * Release popup back to pool
     */
    releaseToPool(item) {
        item.inUse = false;
        item.el.style.opacity = '0';
        item.el.style.animation = 'none';
        item.timeoutId = null;
    }

    /**
     * Inject required CSS animations (only once)
     */
    ensureStyles() {
        if (this.stylesInjected) return;
        if (document.querySelector('#popup-manager-styles')) {
            this.stylesInjected = true;
            return;
        }

        // Use simpler shadows on mobile
        const accelShadow = this.isMobile
            ? '0 1px 2px rgba(0, 0, 0, 0.5)'
            : '0 0 10px rgba(80, 255, 80, 0.8)';
        const boostShadow = this.isMobile
            ? '0 1px 2px rgba(0, 0, 0, 0.5)'
            : '0 0 10px rgba(255, 51, 51, 0.8)';
        const maxShadow = this.isMobile
            ? '0 2px 4px rgba(0, 0, 0, 0.5)'
            : '0 0 20px rgba(255, 170, 0, 1)';
        const waveShadow = this.isMobile
            ? '0 2px 4px rgba(0, 0, 0, 0.5)'
            : '0 0 20px rgba(255, 165, 0, 0.8), 0 0 40px rgba(255, 165, 0, 0.6), 0 0 60px rgba(255, 165, 0, 0.4)';

        const style = document.createElement('style');
        style.id = 'popup-manager-styles';
        style.textContent = `
            @keyframes popupChaotic {
                0% {
                    opacity: 1;
                    transform: translate(-50%, -50%) scale(1) rotate(0deg) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(calc(-50% + var(--offset-x)), calc(-50% + var(--offset-y))) scale(1.8) rotate(var(--rotation)) translateZ(0);
                }
            }

            @keyframes popupRiseAndFade {
                0% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(-50%, var(--rise-distance, -80px)) scale(var(--end-scale, 1.5)) translateZ(0);
                }
            }

            @keyframes popupScrap {
                0% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1) translateZ(0);
                }
                60% {
                    opacity: 1;
                    transform: translate(-50%, calc(var(--rise-distance, -70px) * 0.7)) scale(var(--end-scale, 1)) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(-50%, var(--rise-distance, -70px)) scale(var(--end-scale, 1)) translateZ(0);
                }
            }

            @keyframes popupScrapSway {
                0% {
                    opacity: 1;
                    transform: translate(-50%, 0) translateZ(0);
                }
                15% {
                    opacity: 1;
                    transform: translate(calc(-50% + 5px), calc(var(--rise-distance, -70px) * 0.18)) translateZ(0);
                }
                35% {
                    opacity: 1;
                    transform: translate(calc(-50% - 4px), calc(var(--rise-distance, -70px) * 0.4)) translateZ(0);
                }
                55% {
                    opacity: 1;
                    transform: translate(calc(-50% + 3px), calc(var(--rise-distance, -70px) * 0.6)) translateZ(0);
                }
                75% {
                    opacity: 1;
                    transform: translate(calc(-50% - 2px), calc(var(--rise-distance, -70px) * 0.82)) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(-50%, var(--rise-distance, -70px)) translateZ(0);
                }
            }

            @keyframes popupMaxSpeed {
                0% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(-50%, -120px) scale(2) translateZ(0);
                }
            }

            @keyframes popupWaveEvent {
                0% {
                    opacity: 0;
                    transform: translate(-50%, 100px) scale(0.5) translateZ(0);
                }
                15% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1.2) translateZ(0);
                }
                30% {
                    transform: translate(-50%, 0) scale(1) translateZ(0);
                }
                70% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(-50%, -100px) scale(0.8) translateZ(0);
                }
            }

            @keyframes popupItemObtained {
                0% {
                    opacity: 0;
                    transform: translate(-50%, -50%) scale(0.8) translateZ(0);
                }
                10% {
                    opacity: 1;
                    transform: translate(-50%, -50%) scale(1) translateZ(0);
                }
                90% {
                    opacity: 1;
                    transform: translate(-50%, -50%) scale(1) translateZ(0);
                }
                100% {
                    opacity: 0;
                    transform: translate(-50%, -50%) scale(0.8) translateZ(0);
                }
            }

            .popup-accel-green {
                color: #50ff50;
                text-shadow: ${accelShadow};
            }

            .popup-accel-red {
                color: #ff3333;
                text-shadow: ${boostShadow};
            }

            .popup-miles-style {
                color: #50ff50;
                text-shadow: ${accelShadow};
            }

            .popup-max-style {
                color: #ffaa00;
                text-shadow: ${maxShadow};
            }

            .popup-wave-style {
                color: #ffa500;
                text-shadow: ${waveShadow};
            }

            .popup-scrap-miles {
                color: #50ff50;
                font-size: 26px;
                text-shadow: 0 0 12px rgba(80, 255, 80, 0.9);
            }

            .popup-scrap-bonus {
                color: #4ec9b0;
                font-size: 14px;
                background: rgba(0, 0, 0, 0.85);
                padding: 4px 10px;
                border: 1px solid rgba(78, 201, 176, 0.5);
                border-radius: 4px;
                text-shadow: 0 0 6px rgba(78, 201, 176, 0.7);
            }
        `;
        document.head.appendChild(style);
        this.stylesInjected = true;
    }

    /**
     * Show acceleration popup (+X MPH)
     * @param {number} x - Click X position
     * @param {number} y - Click Y position
     * @param {number} amount - Speed increase amount
     */
    showAcceleration(x, y, amount) {
        const { item, nextIndex } = this.getFromPool(this.accelPool, this.accelPoolIndex);
        this.accelPoolIndex = nextIndex;

        // Random chaotic direction and distance
        const angle = Math.random() * Math.PI * 2;
        const distance = 80 + Math.random() * 120;
        const offsetX = Math.cos(angle) * distance;
        const offsetY = Math.sin(angle) * distance;
        const rotation = (Math.random() - 0.5) * 60;

        const popup = item.el;
        popup.textContent = `+${amount.toFixed(1)} MPH`;
        popup.className = 'popup-accel popup-accel-green';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.setProperty('--offset-x', `${offsetX}px`);
        popup.style.setProperty('--offset-y', `${offsetY}px`);
        popup.style.setProperty('--rotation', `${rotation}deg`);
        popup.style.animation = 'popupChaotic 1.2s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1200);
    }

    /**
     * Show boost popup (red overboost)
     * @param {number} x - Click X position
     * @param {number} y - Click Y position
     * @param {number} amount - Boost amount
     */
    showBoost(x, y, amount) {
        const { item, nextIndex } = this.getFromPool(this.accelPool, this.accelPoolIndex);
        this.accelPoolIndex = nextIndex;

        // Upward-only direction (angles between π and 2π)
        const angle = Math.PI + (Math.random() * Math.PI);
        const distance = 80 + Math.random() * 120;
        const offsetX = Math.cos(angle) * distance;
        const offsetY = Math.sin(angle) * distance;
        const rotation = (Math.random() - 0.5) * 60;

        const popup = item.el;
        popup.textContent = `+${amount.toFixed(1)} MPH BOOST`;
        popup.className = 'popup-accel popup-accel-red';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.setProperty('--offset-x', `${offsetX}px`);
        popup.style.setProperty('--offset-y', `${offsetY}px`);
        popup.style.setProperty('--rotation', `${rotation}deg`);
        popup.style.animation = 'popupChaotic 1.2s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1200);
    }

    /**
     * Show new mile popup (+X above trip button)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     * @param {number} miles - Miles gained
     */
    showMiles(x, y, miles) {
        const { item, nextIndex } = this.getFromPool(this.milesPool, this.milesPoolIndex);
        this.milesPoolIndex = nextIndex;

        const popup = item.el;
        const milesDisplay = Number.isInteger(miles) ? miles : miles.toFixed(2);
        popup.textContent = `+${milesDisplay}`;
        popup.className = 'popup-miles popup-miles-style';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.setProperty('--rise-distance', '-80px');
        popup.style.setProperty('--end-scale', '1.5');
        popup.style.animation = 'popupRiseAndFade 1.5s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1500);
    }

    /**
     * Show max speed popup (MAX! above drive button)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     */
    showMaxSpeed(x, y) {
        // Max speed popup is rare, no need to pool
        const popup = document.createElement('div');
        popup.textContent = 'MAX!';
        popup.className = 'popup-max-style';
        popup.style.cssText = `
            position: absolute;
            left: ${x}px;
            top: ${y}px;
            font-family: 'Courier New', monospace;
            font-size: 48px;
            font-weight: bold;
            pointer-events: none;
            will-change: transform, opacity;
            transform: translateZ(0);
            animation: popupMaxSpeed 1.5s ease-out forwards;
        `;

        this.container.appendChild(popup);
        setTimeout(() => popup.remove(), 1500);
    }

    /**
     * Show new wave event notification
     */
    showWaveEvent() {
        // Wave events are rare, no need to pool
        const popup = document.createElement('div');
        popup.textContent = 'LOT FOUND!';
        popup.className = 'popup-wave-style';
        popup.style.cssText = `
            position: absolute;
            left: 50%;
            bottom: 25%;
            font-family: 'Courier New', monospace;
            font-size: 72px;
            font-weight: bold;
            pointer-events: none;
            z-index: 1500;
            will-change: transform, opacity;
            transform: translateZ(0);
            animation: popupWaveEvent 3s ease-out forwards;
        `;

        this.container.appendChild(popup);
        setTimeout(() => popup.remove(), 3000);
    }

    /**
     * Show item obtained notification
     * @param {object} item - The item object
     * @param {string} rarityColor - Color for the item rarity
     * @param {function} createItemDisplay - Function to create item display content
     */
    showItemObtained(item, rarityColor, createItemDisplay) {
        // Item notifications are rare, no need to pool
        const boxShadow = this.isMobile
            ? `0 4px 20px ${rarityColor}`
            : `0 0 30px ${rarityColor}`;

        // Create overlay to capture clicks/taps to dismiss
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            inset: 0;
            z-index: 399;
            cursor: pointer;
        `;

        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) translateZ(0);
            background: rgba(20, 20, 30, 0.95);
            border: 3px solid ${rarityColor};
            border-radius: 12px;
            padding: 30px;
            z-index: 400;
            font-family: 'Courier New', monospace;
            text-align: center;
            box-shadow: ${boxShadow};
            will-change: transform, opacity;
            animation: popupItemObtained 5s ease-out forwards;
            cursor: pointer;
        `;

        const title = document.createElement('div');
        title.textContent = 'DASH ITEM OBTAINED!';
        title.style.cssText = `
            font-size: 24px;
            color: #50ff50;
            margin-bottom: 15px;
        `;
        notification.appendChild(title);

        // Use callback to create item display (keeps dependency on GameUI's createItemDisplay)
        if (createItemDisplay) {
            createItemDisplay(item, notification, false, null);
        }

        // Dismiss function
        const dismiss = () => {
            overlay.remove();
            notification.remove();
        };

        // Click/tap anywhere to dismiss
        overlay.addEventListener('click', dismiss);
        notification.addEventListener('click', dismiss);

        document.body.appendChild(overlay);
        document.body.appendChild(notification);

        // Auto-dismiss after 5 seconds
        setTimeout(dismiss, 5000);
    }

    /**
     * Show scrap result - spawns mile popup + optional bonus popup
     * @param {number} x - X position (center)
     * @param {number} y - Y position (top)
     * @param {number} miles - Total miles earned
     * @param {number} bonusPercent - Bonus % (0 if no skill)
     * @param {number} zIndex - Optional z-index (default 250, above modals)
     */
    showScrap(x, y, miles, bonusPercent = 0, zIndex = 250) {
        this.showScrapMiles(x, y, miles, zIndex);
        if (bonusPercent > 0) {
            // Queue bonus popup after miles popup has risen
            setTimeout(() => {
                this.showScrapBonus(x, y, bonusPercent, zIndex);
            }, 400);
        }
    }

    /**
     * Show scrap result for wavedust - spawns wavedust popup + optional bonus popup
     * @param {number} x - X position (center)
     * @param {number} y - Y position (top)
     * @param {number} wavedust - Total wavedust earned
     * @param {number} bonusPercent - Bonus % (0 if no skill)
     * @param {number} zIndex - Optional z-index (default 250, above modals)
     */
    showScrapWavedust(x, y, wavedust, bonusPercent = 0, zIndex = 250) {
        this.showScrapWavedustAmount(x, y, wavedust, zIndex);
        if (bonusPercent > 0) {
            // Queue bonus popup after wavedust popup has risen
            setTimeout(() => {
                this.showScrapBonus(x, y, bonusPercent, zIndex);
            }, 400);
        }
    }

    /**
     * Show scrap wavedust popup (purple +XX ⚙)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     * @param {number} wavedust - Wavedust earned
     * @param {number} zIndex - Optional z-index
     */
    showScrapWavedustAmount(x, y, wavedust, zIndex = 250) {
        const { item, nextIndex } = this.getFromPool(this.scrapPool, this.scrapPoolIndex);
        this.scrapPoolIndex = nextIndex;

        const popup = item.el;
        popup.textContent = `+${wavedust} ⚙`;
        popup.className = 'popup-scrap popup-scrap-wavedust';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.fontSize = '26px';
        popup.style.color = '#a78bfa'; // Purple for wavedust
        popup.style.textShadow = '0 0 10px rgba(167, 139, 250, 0.5)';
        popup.style.zIndex = String(zIndex);
        popup.style.setProperty('--rise-distance', '-90px');
        popup.style.setProperty('--end-scale', '1.1');
        popup.style.animation = 'popupScrap 1.8s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1800);
    }

    /**
     * Show scrap miles popup (green +XX MI)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     * @param {number} miles - Miles earned
     * @param {number} zIndex - Optional z-index
     */
    showScrapMiles(x, y, miles, zIndex = 250) {
        const { item, nextIndex } = this.getFromPool(this.scrapPool, this.scrapPoolIndex);
        this.scrapPoolIndex = nextIndex;

        const popup = item.el;
        popup.textContent = `+${miles} MI`;
        popup.className = 'popup-scrap popup-scrap-miles';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.fontSize = '26px';
        popup.style.zIndex = String(zIndex);
        popup.style.setProperty('--rise-distance', '-90px');
        popup.style.setProperty('--end-scale', '1.1');
        popup.style.animation = 'popupScrap 1.8s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1800);
    }

    /**
     * Show generic bonus popup (teal, for item upgrades etc.)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     * @param {string} text - Text to display
     * @param {number} zIndex - Optional z-index
     */
    showBonusPopup(x, y, text, zIndex = 250) {
        const { item, nextIndex } = this.getFromPool(this.scrapPool, this.scrapPoolIndex);
        this.scrapPoolIndex = nextIndex;

        const popup = item.el;
        popup.textContent = text;
        popup.className = 'popup-scrap popup-scrap-bonus';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.fontSize = '14px';
        popup.style.zIndex = String(zIndex);
        popup.style.setProperty('--rise-distance', '-70px');
        popup.style.setProperty('--end-scale', '1.0');
        popup.style.animation = 'popupScrapSway 2.2s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 2200);
    }

    /**
     * Show scrap bonus popup (teal +X% SCRAP BONUS!)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     * @param {number} percent - Bonus percentage
     * @param {number} zIndex - Optional z-index
     */
    showScrapBonus(x, y, percent, zIndex = 250) {
        const { item, nextIndex } = this.getFromPool(this.scrapPool, this.scrapPoolIndex);
        this.scrapPoolIndex = nextIndex;

        const popup = item.el;
        popup.textContent = `${Math.round(percent)}% SCRAP BONUS!`;
        popup.className = 'popup-scrap popup-scrap-bonus';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.fontSize = '14px';
        popup.style.zIndex = String(zIndex);
        popup.style.setProperty('--rise-distance', '-70px');
        popup.style.setProperty('--end-scale', '1.0');
        popup.style.animation = 'popupScrapSway 2.2s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 2200);
    }

    /**
     * Show salvage crate collection popup (teal +XX ⚙)
     * Used when player collects a salvage crate on the road
     * @param {number} wavedust - Wavedust awarded
     * @param {number} bonusPercent - Bonus % from scrap skill (0 if none)
     */
    showSalvageCrateCollected(wavedust, bonusPercent = 0) {
        // Show in center of screen, above bottom quarter
        const x = window.innerWidth / 2;
        const y = window.innerHeight * 0.6;

        // Main wavedust popup (teal colored)
        const { item, nextIndex } = this.getFromPool(this.scrapPool, this.scrapPoolIndex);
        this.scrapPoolIndex = nextIndex;

        const popup = item.el;
        popup.textContent = `+${wavedust} ⚙`;
        popup.className = 'popup-scrap popup-salvage-crate';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.fontSize = '32px';
        popup.style.color = '#44aa88'; // Teal for salvage crates
        popup.style.textShadow = '0 0 15px rgba(68, 170, 136, 0.8)';
        popup.style.zIndex = '150';  // Game level, same as wave event
        popup.style.setProperty('--rise-distance', '-100px');
        popup.style.setProperty('--end-scale', '1.2');
        popup.style.animation = 'popupScrap 1.5s ease-out forwards';  // Faster than scrap

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1500);

        // Show bonus popup if applicable
        if (bonusPercent > 0) {
            setTimeout(() => {
                this.showSalvageCrateBonus(x, y, bonusPercent);
            }, 300);
        }
    }

    /**
     * Show salvage crate bonus popup (teal +X% SCRAP BONUS!)
     * @param {number} x - Center X position
     * @param {number} y - Top Y position
     * @param {number} percent - Bonus percentage
     */
    showSalvageCrateBonus(x, y, percent) {
        const { item, nextIndex } = this.getFromPool(this.scrapPool, this.scrapPoolIndex);
        this.scrapPoolIndex = nextIndex;

        const popup = item.el;
        popup.textContent = `${Math.round(percent)}% SCRAP BONUS!`;
        popup.className = 'popup-scrap popup-salvage-bonus';
        popup.style.left = `${x}px`;
        popup.style.top = `${y}px`;
        popup.style.fontSize = '14px';
        popup.style.color = '#4ec9b0';
        popup.style.background = 'rgba(0, 0, 0, 0.85)';
        popup.style.padding = '4px 10px';
        popup.style.border = '1px solid rgba(68, 170, 136, 0.5)';
        popup.style.borderRadius = '4px';
        popup.style.textShadow = '0 0 6px rgba(68, 170, 136, 0.7)';
        popup.style.zIndex = '150';
        popup.style.setProperty('--rise-distance', '-60px');
        popup.style.setProperty('--end-scale', '1.0');
        popup.style.animation = 'popupScrapSway 1.8s ease-out forwards';

        item.inUse = true;
        item.timeoutId = setTimeout(() => this.releaseToPool(item), 1800);
    }
}
